import { Alert, Box, Button, Typography } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AuthFormWrapper from '../../templates/auth-form-wrapper';
import { PasswordStepFormValues } from '../../templates/login/schema/login-schema';
import AuthFormHeader from '../auth-form-header';

import { LoadingButton } from '@/components/atoms/loading-button/loading-button';
import PasswordInput from '@/components/atoms/password-input/password-input';
import { useKeyDown } from '@/hooks/use-key-down';
import { Logo } from '@/icons/logo';

interface Props {
  onSubmit: (values: PasswordStepFormValues) => Promise<void>;
  onBackButtonClick: () => void;
}

export const PasswordStep = ({ onSubmit, onBackButtonClick }: Props) => {
  const { t } = useTranslation(['auth', 'common']);
  const { control, handleSubmit } = useFormContext<PasswordStepFormValues>();
  const { isValid, isSubmitting, errors } = useFormState<PasswordStepFormValues>();
  const navigate = useNavigate({ from: '/login' });

  useKeyDown('Enter', handleSubmit(onSubmit));

  return (
    <AuthFormWrapper>
      <AuthFormHeader
        title={t('auth:login.title')}
        description={t('auth:login.contentPassword')}
        icon={<Logo />}
      />
      <Box>
        <Controller
          name="password"
          control={control}
          render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
            <PasswordInput
              label={t('auth:password')}
              fullWidth
              required
              inputRef={ref}
              onBlur={onBlur}
              onChange={onChange}
              value={value}
              type="password"
              autoComplete="password"
              error={!!error}
              helperText={error?.message}
              sx={{ mb: 1 }}
              inputProps={{
                'data-testid': 'password-input',
              }}
            />
          )}
        />

        {errors?.root?.message ? <Alert severity="error">{errors.root.message}</Alert> : null}

        <Typography
          variant="subtitle2"
          sx={{ cursor: 'pointer', color: '#6EBAD3' }}
          onClick={() => navigate({ to: '/forgot-password' })}>
          {t('auth:login.forgotYourPassword')}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'end', gap: 2 }}>
        <Button variant="outlined" onClick={onBackButtonClick}>
          {t('common:button.back')}
        </Button>
        <LoadingButton
          variant="contained"
          size="medium"
          disabled={!isValid}
          loading={isSubmitting}
          onClick={handleSubmit(onSubmit)}>
          {t('auth:login.loginButton')}
        </LoadingButton>
      </Box>
    </AuthFormWrapper>
  );
};
